import { Routes, Route, useLocation } from "react-router-dom";
import { lazy, useEffect } from "react";

import { useNavigationFeedback } from "./contexts/NavigationFeedbackContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import MainLayout from "./layouts/MainLayout";

import { lazyImport } from "./utils/lazyImport";
import StagingGate from "./layouts/StagingGate";

const withNavigationCompletion = (Component) => {
  const WrappedComponent = (props) => {
    const { finishNavigation } = useNavigationFeedback();
    const location = useLocation();

    useEffect(() => {
      finishNavigation();
    }, [finishNavigation, location.pathname, location.search, location.hash]);

    return <Component {...props} />;
  };

  WrappedComponent.displayName = `withNavigationCompletion(${
    Component.displayName || Component.name || "Component"
  })`;

  return WrappedComponent;
};

const HomePage = lazy(() => import("./pages/home/homePage"));
const NotFound = lazy(() => import("./features/shared/components/404page"));
const MintanacePage = lazy(() => import("./pages/mintanacePage"));

const ProductList = lazyImport(
  () => import("features/Catalog/index"),
  "ProductList",
);
const ProductPage = lazyImport(
  () => import("features/Catalog/index"),
  "ProductPage",
);
const CartPage = lazy(() => import("./features/cart/pages/CartPage"));
const Signup = lazy(() => import("./features/Auth-copy/pages/Signup/Signup"));
const Login = lazy(() => import("./features/Auth-copy/pages/Login/Login"));

const ForgetPassword = lazy(
  () => import("./features/Auth-copy/pages/ForgetPassword/ForgetPassword"),
);
const ResetEmailSent = lazy(
  () => import("./features/Auth-copy/pages/ResetEmailSent/ResetEmailSent"),
);
const ResetPassword = lazy(
  () => import("./features/Auth-copy/pages/ResetPassword/ResetPassword"),
);
const InvalidToken = lazy(
  () => import("./features/Auth-copy/pages/InvalidToken/InvalidToken"),
);
const ResetPasswordSuccess = lazy(
  () =>
    import("./features/Auth-copy/pages/ResetPasswordSuccess/ResetPasswordSuccess"),
);
const PaymentResult = lazy(
  () => import("./features/Checkout/pages/PaymentResult"),
);
const Order = lazy(() => import("./features/OrderHistory/pages/Order"));
const OrdersList = lazy(
  () => import("./features/OrderHistory/pages/OrdersList"),
);
const AboutPage = lazy(() =>
  import("./features/Legal").then((module) => ({ default: module.AboutPage })),
);
const TermsPage = lazy(() =>
  import("./features/Legal").then((module) => ({ default: module.TermsPage })),
);
const StrategyLandingPage = lazy(
  () => import("./features/StrategyLandingPage/page/StrategyLandingPage"),
);
const DisclaimerPage = lazy(() =>
  import("./features/Legal").then((module) => ({
    default: module.DisclaimerPage,
  })),
);
const CertificationPage = lazy(() =>
  import("./features/Legal").then((module) => ({
    default: module.CertificationPage,
  })),
);
const PrivacyPolicyPage = lazy(() =>
  import("./features/Legal").then((module) => ({
    default: module.PrivacyPolicyPage,
  })),
);
const ShippingPolicyPage = lazy(() =>
  import("./features/Legal").then((module) => ({
    default: module.ShippingPolicyPage,
  })),
);
const ReturnsPolicyPage = lazy(() =>
  import("./features/Legal").then((module) => ({
    default: module.ReturnsPolicyPage,
  })),
);
const BundlePage = lazy(() =>
  import("./features/Bundle/index").then((module) => ({
    default: module.BundlePage,
  })),
);

const EligibilityCheckerPage = lazy(
  () => import("./features/EligibilityChecker/page/EligibilityChecker"),
);
const FitnessAssessmentPage = lazy(
  () => import("./features/FitnessAssessment/page/FitnessAssessmentPage"),
);

const HomePageWithFeedback = withNavigationCompletion(HomePage);
const NotFoundWithFeedback = withNavigationCompletion(NotFound);
const MintanacePageWithFeedback = withNavigationCompletion(MintanacePage);
const ProductListWithFeedback = withNavigationCompletion(ProductList);
const ProductPageWithFeedback = withNavigationCompletion(ProductPage);
const BundlePageFeedback = withNavigationCompletion(BundlePage);
const StrategyLandingPageFeedback =
  withNavigationCompletion(StrategyLandingPage);
const CartPageWithFeedback = withNavigationCompletion(CartPage);
const SignupWithFeedback = withNavigationCompletion(Signup);
const LoginWithFeedback = withNavigationCompletion(Login);
const ForgetPasswordWithFeedback = withNavigationCompletion(ForgetPassword);
const ResetEmailSentWithFeedback = withNavigationCompletion(ResetEmailSent);
const ResetPasswordWithFeedback = withNavigationCompletion(ResetPassword);
const InvalidTokenWithFeedback = withNavigationCompletion(InvalidToken);
const ResetPasswordSuccessWithFeedback =
  withNavigationCompletion(ResetPasswordSuccess);
const PaymentResultWithFeedback = withNavigationCompletion(PaymentResult);
const OrderWithFeedback = withNavigationCompletion(Order);
const OrdersListWithFeedback = withNavigationCompletion(OrdersList);
const AboutPageWithFeedback = withNavigationCompletion(AboutPage);
const TermsPageWithFeedback = withNavigationCompletion(TermsPage);
const DisclaimerPageWithFeedback = withNavigationCompletion(DisclaimerPage);
const CertificationPageWithFeedback =
  withNavigationCompletion(CertificationPage);
const PrivacyPolicyPageWithFeedback =
  withNavigationCompletion(PrivacyPolicyPage);
const ShippingPolicyPageWithFeedback =
  withNavigationCompletion(ShippingPolicyPage);
const ReturnsPolicyPageWithFeedback =
  withNavigationCompletion(ReturnsPolicyPage);
const EligibilityCheckerPageWithFeedback = withNavigationCompletion(
  EligibilityCheckerPage,
);
const FitnessAssessmentPageWithFeedback = withNavigationCompletion(
  FitnessAssessmentPage,
);

const isMaintenance = false;

const AppRoutes = () => {
  return (
    <Routes>
      {isMaintenance ? (
        <Route path="*" element={<MintanacePageWithFeedback />} />
      ) : (
        <Route element={<StagingGate />}>
          <Route element={<MainLayout />}>
            <Route path="/" element={<HomePageWithFeedback />} />
            <Route path="/shop" element={<ProductListWithFeedback />} />
            <Route path="/shop/:id" element={<ProductPageWithFeedback />} />
            <Route path="/bundle" element={<BundlePageFeedback />} />

            <Route path="/about" element={<AboutPageWithFeedback />} />
            <Route
              path="/strategy-chat"
              element={<StrategyLandingPageFeedback />}
            />
            <Route path="/terms" element={<TermsPageWithFeedback />} />
            <Route
              path="/disclaimer"
              element={<DisclaimerPageWithFeedback />}
            />
            <Route
              path="/certification-details"
              element={<CertificationPageWithFeedback />}
            />
            <Route
              path="/check-eligibility"
              element={<EligibilityCheckerPageWithFeedback />}
            />
            <Route
              path="/fitness-assessment"
              element={<FitnessAssessmentPageWithFeedback />}
            />
            <Route
              path="/privacy-policy"
              element={<PrivacyPolicyPageWithFeedback />}
            />
            <Route
              path="/shipping-policy"
              element={<ShippingPolicyPageWithFeedback />}
            />
            <Route
              path="/returns-policy"
              element={<ReturnsPolicyPageWithFeedback />}
            />
            <Route path="/login" element={<LoginWithFeedback />} />
            <Route path="/signup" element={<SignupWithFeedback />} />
            <Route
              path="/forget-password"
              element={<ForgetPasswordWithFeedback />}
            />
            <Route
              path="/reset-email-sent"
              element={<ResetEmailSentWithFeedback />}
            />
            <Route
              path="/reset-password"
              element={<ResetPasswordWithFeedback />}
            />
            <Route
              path="/token-invalid"
              element={<InvalidTokenWithFeedback />}
            />
            <Route
              path="/reset-password-success"
              element={<ResetPasswordSuccessWithFeedback />}
            />
            <Route
              path="/payment-result"
              element={<PaymentResultWithFeedback />}
            />
            <Route path="/cart" element={<CartPageWithFeedback />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/orders">
                <Route index element={<OrdersListWithFeedback />} />
                <Route path=":id" element={<OrderWithFeedback />} />
              </Route>
            </Route>
            <Route path="*" element={<NotFoundWithFeedback />} />
          </Route>
        </Route>
      )}
    </Routes>
  );
};

export default AppRoutes;
